import React, { useState } from "react"
import classNames from "classnames"

const SIDEBAR_MIN_WIDTH = 360
// Keep enough room beside the sidebar for the source view's toolbar to fit
const SOURCE_VIEW_MIN_WIDTH = 560
const KEYBOARD_STEP = 16

// CSS width for a resized sidebar. Applies the same limits as dragging, so the
// source view keeps its room if the window gets narrower.
export const sidebarWidthCss = (width: number) =>
  `clamp(${SIDEBAR_MIN_WIDTH}px, ${width}px, 100% - ${SOURCE_VIEW_MIN_WIDTH}px)`

const STORAGE_KEY = "nana:sources-sidebar-width"

export const loadSidebarWidth = (): number | null => {
  try {
    const width = Number(window.localStorage.getItem(STORAGE_KEY))
    return width >= SIDEBAR_MIN_WIDTH ? width : null
  } catch {
    return null
  }
}

export const saveSidebarWidth = (width: number | null) => {
  try {
    if (width == null) window.localStorage.removeItem(STORAGE_KEY)
    else window.localStorage.setItem(STORAGE_KEY, String(width))
  } catch {
    // Storage can be unavailable (e.g. private browsing); the width just won't persist
  }
}

// Sits on the sidebar's right edge (the sidebar must be position: relative).
// Drag or use the arrow keys to resize; double-click to go back to the default width.
export default function SidebarResizeHandle({
  sidebarRef,
  onResize,
  onReset,
}: {
  sidebarRef: React.RefObject<HTMLDivElement | null>
  onResize: (width: number) => void
  onReset: () => void
}) {
  const [dragging, setDragging] = useState(false)

  const clampWidth = (sidebar: HTMLDivElement, width: number) => {
    const containerWidth = sidebar.parentElement?.clientWidth ?? window.innerWidth
    const maxWidth = Math.max(SIDEBAR_MIN_WIDTH, containerWidth - SOURCE_VIEW_MIN_WIDTH)
    return Math.round(Math.min(Math.max(width, SIDEBAR_MIN_WIDTH), maxWidth))
  }

  const startDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    const sidebar = sidebarRef.current
    if (event.button !== 0 || !sidebar) return
    // Stops the drag from selecting text
    event.preventDefault()

    const left = sidebar.getBoundingClientRect().left
    // Only counts as a drag once the pointer moves, so the overlay doesn't
    // cover the handle between the clicks of a double-click
    const onMove = (e: PointerEvent) => {
      setDragging(true)
      onResize(clampWidth(sidebar, e.clientX - left))
    }
    const onEnd = () => {
      setDragging(false)
      window.removeEventListener("pointermove", onMove)
      window.removeEventListener("pointerup", onEnd)
      window.removeEventListener("pointercancel", onEnd)
    }
    window.addEventListener("pointermove", onMove)
    window.addEventListener("pointerup", onEnd)
    window.addEventListener("pointercancel", onEnd)
  }

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const sidebar = sidebarRef.current
    if (!sidebar) return
    const step = { ArrowLeft: -KEYBOARD_STEP, ArrowRight: KEYBOARD_STEP }[event.key]
    if (!step) return
    event.preventDefault()
    onResize(clampWidth(sidebar, sidebar.getBoundingClientRect().width + step))
  }

  return (
    <>
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize sidebar"
        tabIndex={0}
        onPointerDown={startDrag}
        onDoubleClick={onReset}
        onKeyDown={onKeyDown}
        className="group absolute inset-y-0 -right-1 z-10 hidden w-2 cursor-col-resize touch-none focus:outline-none md:block"
        data-testid="sources-sidebar-resize-handle"
      >
        <div
          className={classNames(
            "mx-auto h-full w-0.5 transition-colors group-hover:bg-blue-400 group-focus-visible:bg-blue-500",
            { "bg-blue-500": dragging },
          )}
        />
      </div>
      {/* Keeps the resize cursor everywhere during a drag, and stops the
          source view (which can contain an iframe) from swallowing pointer events */}
      {dragging && <div className="fixed inset-0 z-50 cursor-col-resize" />}
    </>
  )
}
